<template lang="pug">
el-alert(
  v-if="loadError",
  :title="$t('genericError')",
  :closable="false",
  type="error",
  effect="dark"
)
template(v-else)
  div(:class="horizontal ? 'flex flex-wrap gap-4' : ''")
    .checklist-view.mb-8(
      v-for="(list, idx) in checklists",
      :key="list.uuid ?? idx",
      :style="horizontal ? 'flex: 1; min-width: 600px' : ''"
    )
      .flex.flex-row.items-center.mb-4.justify-between
        EditableText.mr-4(
          :content="list.name",
          :disabled="!canEdit",
          :disable-empty="true",
          :save-callback="(name: string) => saveChecklistName(list, name)"
        )
          h1 {{ list.name }}
        .flex.flex-row.items-center.gap-4
          .flex.flex-row.gap-1
            el-button(
              :aria-label="$t('completed')",
              :type="completionFilters[list.uuid ?? list.name]?.completed ? 'success' : 'default'",
              icon="CircleCheck",
              size="small",
              @click="toggleCompletionFilter(list, 'completed')"
            ) {{ $t("completed") }}
            el-button(
              :aria-label="$t('notCompleted')",
              :type="completionFilters[list.uuid ?? list.name]?.notCompleted ? 'primary' : 'default'",
              icon="CircleClose",
              size="small",
              @click="toggleCompletionFilter(list, 'notCompleted')"
            ) {{ $t("notCompleted") }}
          .stats.text-slate-500 {{ $t("nOfMTasksCompleted", { n: completedCount(list), m: trackingCount(list) }) }}
          el-icon.cursor-pointer.mr-2.collapse-checklist(
            v-if="list.uuid",
            @click="switchVisibility(list.uuid)"
          )
            ArrowDownBold(v-if="list.uuid && hiddenLists.includes(list.uuid)")
            ArrowUpBold(v-else)
      .checklist-content(v-if="!list.uuid || !hiddenLists.includes(list.uuid)")
        .checklist-items
          draggable(
            :list="list.items",
            group="tasklist-items",
            item-key="uuid",
            handle=".drag-handle",
            :disabled="!canEdit || isCompletionFilterActive(list)",
            @change="changeHandlerFor(list)($event)"
          )
            template(#item="{ element: item }")
              .checklist-row.mt-4(v-if="isItemVisible(item, list)")
                el-checkbox.tasklist-checkbox.self-start.pt-3(
                  v-if="item.completionTracking",
                  size="large",
                  :disabled="!isTicketInProcess || isLinkedTicket(item)",
                  :model-value="isItemCompleted(item)",
                  @update:model-value="(value: boolean) => toggleItemComplete(item, value)"
                )
                .placeholder(v-else)
                .min-w-0(v-if="item.twin")
                  el-button.flex.w-full.min-w-0.items-center.justify-start.gap-2.mt-2.whitespace-normal.break-words.text-left(
                    type="primary",
                    link,
                    :class="{ 'line-through': isTicketRejected(item) }",
                    @click="openTicketPreview(item)"
                  )
                    TwinField.min-w-0.break-words.text-accent(
                      :twin="getLinkedTwin(item)",
                      :show-text-properties="isLinkedAsset(item)",
                      :link="false"
                    )
                EditableText.min-w-0.break-words(
                  v-else,
                  :content="item.description",
                  :disabled="!canEdit",
                  multiline,
                  :save-callback="(desc: string) => saveItemDescription(item, desc)"
                )
                  .flex.items-center.whitespace-pre-wrap(
                    v-if="item.description",
                    style="min-height: 32px"
                  ) {{ item.description }}
                  .flex.items-center.text-slate-400(
                    v-else,
                    style="min-height: 32px"
                  ) ...
                EditableText.min-w-0.break-words(
                  v-if="item.feedback",
                  :content="item.instance?.feedback ?? ''",
                  :disabled="!isTicketInProcess",
                  :save-callback="(fb: string) => saveItemFeedback(item, fb)"
                )
                  span(v-if="item.instance?.feedback") {{ item.instance?.feedback }}
                  span.text-slate-400(v-else) {{ $t("feedback") }}
                .placeholder(v-else)
                .self-start(v-if="canEdit")
                  el-dropdown(trigger="click")
                    el-button(icon="MoreFilled", circle, text)
                    template(#dropdown)
                      el-dropdown-menu
                        el-dropdown-item(
                          v-if="!item.twin",
                          @click="openTransferDialog(item)"
                        )
                          el-icon
                            Switch
                          span {{ $t("transferToTicket") }}
                        el-dropdown-item(
                          @click="openDeleteItemDialog(list, item)"
                        )
                          el-icon
                            Delete
                          span {{ $t("delete") }}
                .placeholder(v-else)
                .drag-handle.flex.flex-col.cursor-grab.self-start.pt-3(
                  v-if="canEdit && !isCompletionFilterActive(list)"
                )
                  el-icon
                    Rank
                .placeholder(v-else)
                .item-creator.flex.items-center.gap-2.text-sm.text-slate-500(
                  v-if="item.created || (item.createdByUser && getCreatorName(item.createdByUser))"
                )
                  template(
                    v-if="item.createdByUser && getCreatorName(item.createdByUser)"
                  )
                    span {{ getCreatorName(item.createdByUser) }}
                  span(v-if="item.createdByUser && item.created") ⸱
                  template(v-if="item.created")
                    span {{ formatPassedDuration(item.created) }}
                    span ⸱
                    span {{ formatDateTime(item.created) }}

        el-button.mt-6.mb-6(
          v-if="canEdit && list.uuid",
          icon="Plus",
          link,
          @click="openAddItemDialog(list)"
        ) {{ $t("addEntry") }}
el-button(
  v-if="canEdit && !isTicketFinished",
  icon="Plus",
  link,
  @click="openAddChecklistDialog"
) {{ $t("addChecklist") }}
TicketPreviewDrawer(
  v-model="ticketPreviewVisible",
  :ticket="ticketPreview",
  :loading="ticketPreviewLoading",
  :sync-route="!replacePreviewTicket"
)
el-dialog(
  v-model="addChecklistDialogVisible",
  :title="$t('addChecklist')",
  width="400px",
  @opened="focusNewChecklistInput"
)
  el-input(
    ref="newChecklistInputRef",
    v-model="newChecklistName",
    :placeholder="$t('checklistName')",
    @keyup.enter="confirmAddChecklist"
  )
  template(#footer)
    el-button(@click="addChecklistDialogVisible = false") {{ $t("cancel") }}
    el-button(type="primary", @click="confirmAddChecklist") {{ $t("save") }}
el-dialog(
  v-model="addItemDialogVisible",
  :title="$t('addEntry')",
  width="600px",
  @opened="focusNewItemInput"
)
  smx-form
    smx-form-item(:label="$t('description')")
      el-input(
        ref="newItemInputRef",
        v-model="newItemDescription",
        @keyup.enter="confirmAddItem"
      )
    smx-form-item
      el-checkbox(v-model="createTicketForTask") {{ $t("createTicketForTask") }}
    template(v-if="createTicketForTask")
      template(v-if="ticketTemplates.length > 1")
        smx-form-item(:label="$t('ticketTemplate')")
          el-select(v-model="selectedTicketTemplateUuid")
            el-option(
              v-for="template in ticketTemplates",
              :key="template.uuid",
              :label="template.name",
              :value="template.uuid"
            )
      template(
        v-for="property in editableTicketProperties",
        :key="property.uuid"
      )
        smx-form-item(
          :label="property.name",
          :required="isPropertyMandatory(property)"
        )
          TwinPropertyInput(
            :prop="property",
            :model-value="ticketPropertyValues[property.uuid]",
            @update:model-value="(value) => (ticketPropertyValues[property.uuid] = value)"
          )
  template(#footer)
    el-button(@click="addItemDialogVisible = false") {{ $t("cancel") }}
    el-button(type="primary", @click="confirmAddItem") {{ $t("save") }}
el-dialog(
  v-model="transferDialogVisible",
  :title="$t('transferToTicket')",
  width="600px"
)
  smx-form
    template(v-if="ticketTemplates.length > 1")
      smx-form-item(:label="$t('ticketTemplate')")
        el-select(v-model="selectedTicketTemplateUuid")
          el-option(
            v-for="template in ticketTemplates",
            :key="template.uuid",
            :label="template.name",
            :value="template.uuid"
          )
    smx-form-item(:label="$t('ticketDescription')")
      el-input(v-model="ticketDescription", type="textarea", :rows="3")
    template(
      v-for="property in editableTicketProperties",
      :key="property.uuid"
    )
      smx-form-item(
        :label="property.name",
        :required="isPropertyMandatory(property)"
      )
        TwinPropertyInput(
          :prop="property",
          :model-value="ticketPropertyValues[property.uuid]",
          @update:model-value="(value) => (ticketPropertyValues[property.uuid] = value)"
        )
  template(#footer)
    el-button(@click="transferDialogVisible = false") {{ $t("cancel") }}
    el-button(type="primary", @click="confirmTransferToTicket") {{ $t("transferToTicket") }}
el-dialog(
  v-model="deleteItemDialogVisible",
  :title="$t('confirmDelete')",
  width="400px"
)
  template(#footer)
    el-button(@click="deleteItemDialogVisible = false") {{ $t("cancel") }}
    el-button(type="danger", @click="confirmDeleteItem") {{ $t("delete") }}
</template>

<script setup lang="ts">
import { computed, inject, onMounted, ref, watch } from "vue";
import {
  Maintenance,
  TwinManagement,
  type MaintenanceTypes,
  type TwinManagementTypes,
} from "@inctec/smx-ui-client";
import draggable from "vuedraggable";
import {
  type TwinProperty,
  type TwinResponse,
} from "@shared/services/twinManagement";
import TicketPreviewDrawer from "@shared/components/tickets/TicketPreviewDrawer.vue";
import { useTwinStore } from "@shared/stores/twinManagement";
import { type TicketManagementAuth } from "@shared/maintenance/hooks/maintenanceAuth";
import EditableText from "@shared/components/elements/EditableText.vue";
import Avatar from "@shared/components/elements/Avatar.vue";
import TwinPropertyInput from "@shared/components/twinManagement/components/TwinPropertyInput.vue";
import { useI18n } from "vue-i18n";
import { ElNotification } from "element-plus";
import { showResponseErrorToUser } from "@shared/util/genericResponseErrors";
import { formatDateTime, formatPassedDuration } from "@shared/util/formatting";
import { statuses, ticketProps } from "../constants";
import { useAuthStore } from "../../../stores/auth";
import { ticketPreviewNavigationKey } from "../ticketPreviewNavigation";
import { prefix as assetPrefix } from "@shared/assetManagement/constants";
import TwinField from "@shared/components/twinManagement/components/TwinField.vue";

const props = defineProps<{
  ticket: TwinResponse;
  useAuth?: () => TicketManagementAuth;
  horizontal?: boolean;
  ticketTemplateQuery?: Omit<
    TwinManagementTypes.SearchTwinsData["body"],
    "isTemplate" | "active" | "showProperties"
  >;
}>();

const auth = props.useAuth?.() ?? {
  isAdmin: () => false,
  isTicketTypeManager: () => false,
  isTicketTypeProcessor: () => false,
  isPlanner: () => false,
  isTicketCreator: () => false,
  isUser: () => false,
};
const { t } = useI18n();
const authStore = useAuthStore();
const twinStore = useTwinStore();
const replacePreviewTicket = inject(ticketPreviewNavigationKey, undefined);

// ---- Local types ----

type ItemInstance = {
  todoInstanceUuid?: string | null;
  completed?: boolean | null;
  feedback?: string | null;
  status?: string | null;
  createdAt?: string | null;
};

type TasklistItem = NonNullable<
  MaintenanceTypes.TicketTodoListInstancesResponse[number]["items"]
>[number];
type ItemCreator = TasklistItem["createdByUser"];
type TasklistItemTwin = NonNullable<MaintenanceTypes.TasklistItem["twin"]> & {
  type?: string;
};
type AssetTwin = NonNullable<
  TwinManagementTypes.SearchTwinsResponse["result"]
>[number];

type LocalItem = {
  uuid: string;
  todoListUuid: string;
  description: string;
  completionTracking: boolean;
  feedback: boolean;
  ordering: number;
  instance: ItemInstance | null;
  twin?: TasklistItemTwin;
  createdByUser?: ItemCreator;
  created: string | null;
};

type TicketTemplate = TwinResponse;
type TicketTwinProperty = {
  customId: string;
  value: string | number | string[] | number[];
};
type TicketTwin = {
  templateUuid: string;
  name: string;
  description: string;
  properties: TicketTwinProperty[];
};

type LocalChecklist = {
  uuid: string | undefined;
  name: string;
  ordering: number;
  items: LocalItem[];
};

type DragChangeEvent = {
  added?: { element: LocalItem; newIndex: number };
  removed?: { element: LocalItem; oldIndex: number };
  moved?: { element: LocalItem; oldIndex: number; newIndex: number };
};

type CompletionFilter = {
  completed: boolean;
  notCompleted: boolean;
};

// ---- State ----

const checklists = ref<LocalChecklist[]>([]);
const loadError = ref(false);
const hiddenLists = ref<string[]>([]);
const completionFilters = ref<Record<string, CompletionFilter>>({});
const addItemDialogVisible = ref(false);
const pendingAddItemList = ref<LocalChecklist | null>(null);
const newItemDescription = ref("");
const newItemInputRef = ref<{ focus(): void } | null>(null);
const createTicketForTask = ref(false);
const transferDialogVisible = ref(false);
const transferItem = ref<LocalItem | null>(null);
const deleteItemDialogVisible = ref(false);
const pendingDeleteItem = ref<{
  list: LocalChecklist;
  item: LocalItem;
} | null>(null);
const ticketTemplates = ref<TicketTemplate[]>([]);
const selectedTicketTemplateUuid = ref<string | null>(null);
const ticketDescription = ref("");
const ticketPropertyValues = ref<Record<string, unknown>>({});
const addChecklistDialogVisible = ref(false);
const newChecklistName = ref("");
const newChecklistInputRef = ref<{ focus(): void } | null>(null);
const ticketPreviewId = ref<string | null>(null);
const ticketPreviewVisible = ref(false);
const ticketPreviewLoading = ref(false);
const assetTwins = ref<Map<string, AssetTwin>>(new Map());

// ---- Permissions ----

const isAdmin = computed(() => auth.isAdmin());

const ticketStatus = computed(
  () =>
    props.ticket.properties?.find((p) => p.customId === ticketProps.status)
      ?.propertyValue?.value as string | undefined,
);

const isTicketInProcess = computed(
  () => ticketStatus.value === statuses.inProcess,
);

const isBeforeCompleted = computed(() =>
  [statuses.draft, statuses.planned, statuses.new, statuses.inProcess].includes(
    ticketStatus.value ?? "",
  ),
);

const isTicketFinished = computed(() =>
  [statuses.completed, statuses.confirmed, statuses.rejected].includes(
    ticketStatus.value ?? "",
  ),
);

const isAssignee = computed(() => {
  const assignees = props.ticket.properties?.find(
    (p) => p.customId === "assignee",
  )?.propertyValue?.value as string[] | undefined;
  return (
    authStore.userId !== null &&
    (assignees?.includes(authStore.userId) ?? false)
  );
});

const canEdit = computed(
  () => (isAdmin.value || isAssignee.value) && isBeforeCompleted.value,
);

const fallbackTicketTemplateQuery = computed(() => {
  const typeSegments = props.ticket.type.split(".");
  return {
    type:
      typeSegments.length > 1
        ? `${typeSegments.slice(0, -1).join(".")}.*`
        : `${props.ticket.type}.*`,
  };
});

const selectedTicketTemplate = computed(() =>
  ticketTemplates.value.find(
    (template) => template.uuid === selectedTicketTemplateUuid.value,
  ),
);

const editableTicketProperties = computed(() =>
  (selectedTicketTemplate.value?.properties ?? [])
    .filter(
      (property) =>
        property.isUserRelevant &&
        !property.constraint?.settings?.notUserEditable &&
        property.customId !== ticketProps.status,
    )
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
);

const ticketPreview = computed(() => {
  if (!ticketPreviewId.value) {
    return null;
  }
  const ticket = twinStore.getTwinLoadingState(ticketPreviewId.value).result;
  return ticket?.uuid === ticketPreviewId.value ? ticket : null;
});

// ---- Helpers ----

function completedCount(list: LocalChecklist): number {
  return list.items.filter((i) => i.completionTracking && isItemCompleted(i))
    .length;
}

function trackingCount(list: LocalChecklist): number {
  return list.items.filter((i) => i.completionTracking).length;
}

function switchVisibility(uuid: string) {
  const idx = hiddenLists.value.indexOf(uuid);
  if (idx >= 0) {
    hiddenLists.value.splice(idx, 1);
  } else {
    hiddenLists.value.push(uuid);
  }
}

function getCompletionFilter(list: LocalChecklist): CompletionFilter {
  const key = list.uuid ?? list.name;
  return (
    completionFilters.value[key] ?? {
      completed: false,
      notCompleted: false,
    }
  );
}

function toggleCompletionFilter(
  list: LocalChecklist,
  filter: keyof CompletionFilter,
) {
  const filters = { ...getCompletionFilter(list) };
  filters[filter] = !filters[filter];
  completionFilters.value[list.uuid ?? list.name] = filters;
}

function isCompletionFilterActive(list: LocalChecklist): boolean {
  const filters = getCompletionFilter(list);
  return filters.completed || filters.notCompleted;
}

function isItemVisible(item: LocalItem, list: LocalChecklist): boolean {
  const filters = getCompletionFilter(list);
  if (!filters.completed && !filters.notCompleted) return true;
  return isItemCompleted(item) ? filters.completed : filters.notCompleted;
}

function isLinkedAsset(item: LocalItem): boolean {
  return item.twin?.type?.startsWith(assetPrefix) ?? false;
}

function isLinkedTicket(item: LocalItem): boolean {
  return /\.ticket/.test(item.twin?.type ?? "");
}

function getLinkedTwin(
  item: LocalItem,
): TasklistItemTwin | AssetTwin | undefined {
  if (!isLinkedAsset(item) || !item.twin) {
    return item.twin;
  }
  return assetTwins.value.get(item.twin.uuid) ?? item.twin;
}

// NOTE: the ticket status might be updated in the side panel while this page is open, thus we check if the ticket is in the cache
// and if yes, we use its status instead of the status we got from the backend.
function getLinkedTicketStatus(item: LocalItem): string | undefined {
  if (!isLinkedTicket(item) || !item.twin) return undefined;
  const status = twinStore
    .getTwinLoadingState(item.twin.uuid)
    .result?.properties?.find(
      (property) => property.customId === ticketProps.status,
    )?.propertyValue?.value;
  return typeof status === "string" ? status : (item.twin.status ?? undefined);
}

function isItemCompleted(item: LocalItem): boolean {
  if (isLinkedTicket(item)) {
    return [statuses.confirmed, statuses.completed].includes(
      getLinkedTicketStatus(item) ?? "",
    );
  }
  return item.instance?.completed ?? false;
}

function isTicketRejected(item: LocalItem): boolean {
  return getLinkedTicketStatus(item) === statuses.rejected;
}

function getCreatorName(creator: ItemCreator): string {
  return (
    [creator?.firstName, creator?.lastName].filter(Boolean).join(" ") ||
    creator?.username ||
    creator?.email ||
    ""
  );
}

async function openTicketPreview(item: LocalItem) {
  if (!item.twin) return;
  if (replacePreviewTicket) {
    try {
      await replacePreviewTicket(item.twin.uuid);
    } catch (e) {
      showResponseErrorToUser(e, t);
    }
    return;
  }
  ticketPreviewId.value = item.twin.uuid;
  ticketPreviewVisible.value = true;
  ticketPreviewLoading.value = true;
  try {
    await twinStore.loadTwinCached(item.twin.uuid);
  } catch (e) {
    ticketPreviewVisible.value = false;
    showResponseErrorToUser(e, t);
  } finally {
    ticketPreviewLoading.value = false;
  }
}

function resetTicketForm() {
  selectedTicketTemplateUuid.value = null;
  ticketDescription.value = "";
  ticketPropertyValues.value = {};
}

async function loadTicketTemplates() {
  const response = await TwinManagement.searchTwins({
    query: { page: 1, pageSize: 100 },
    body: {
      ...(props.ticketTemplateQuery ?? fallbackTicketTemplateQuery.value),
      isTemplate: true,
      active: true,
      showProperties: true,
    },
  });
  ticketTemplates.value = (response.data?.result ??
    []) as unknown as TicketTemplate[];
  if (ticketTemplates.value.length === 1) {
    selectedTicketTemplateUuid.value = ticketTemplates.value[0]?.uuid ?? null;
  }
}

function isPropertyMandatory(property: TwinProperty): boolean {
  return property.constraint?.settings?.mandatory ?? false;
}

function hasPropertyValue(value: unknown): boolean {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "string") return value.trim() !== "";
  return value !== null && value !== undefined;
}

function getTicketTwin(
  name: string,
  description = ticketDescription.value,
): TicketTwin | null {
  const template = selectedTicketTemplate.value;
  if (!template) {
    ElNotification({
      title: t("error"),
      message: t("templateNotFound"),
      type: "error",
    });
    return null;
  }

  const missingRequiredFields = editableTicketProperties.value.filter(
    (property) =>
      isPropertyMandatory(property) &&
      !hasPropertyValue(ticketPropertyValues.value[property.uuid]),
  );
  if (missingRequiredFields.length > 0) {
    ElNotification({
      title: t("error"),
      message: `${t("followingFieldsAreRequired")} ${missingRequiredFields.map((property) => property.name).join(", ")}`,
      type: "error",
    });
    return null;
  }

  const properties: TicketTwinProperty[] = editableTicketProperties.value
    .filter(
      (property): property is TwinProperty & { customId: string } =>
        property.customId !== undefined &&
        hasPropertyValue(ticketPropertyValues.value[property.uuid]),
    )
    .map((property) => ({
      customId: property.customId,
      value: ticketPropertyValues.value[
        property.uuid
      ] as TicketTwinProperty["value"],
    }));

  properties.push({
    customId: ticketProps.status,
    value: statuses.new,
  });
  if (authStore.userId) {
    properties.push(
      {
        customId: ticketProps.requester,
        value: [authStore.userId],
      },
      {
        customId: ticketProps.createdBy,
        value: [authStore.userId],
      },
    );
  }

  return {
    templateUuid: template.uuid,
    name,
    description,
    properties,
  };
}

watch(selectedTicketTemplateUuid, () => {
  ticketPropertyValues.value = {};
});

watch(createTicketForTask, (shouldCreateTicket) => {
  if (!shouldCreateTicket) return;
  void loadTicketTemplates().catch((e: unknown) =>
    showResponseErrorToUser(e, t),
  );
});

// ---- Drag ordering ----

function changeHandlerFor(list: LocalChecklist) {
  return (evt: DragChangeEvent) => onItemChange(evt, list);
}

async function onItemChange(evt: DragChangeEvent, list: LocalChecklist) {
  if (evt.removed) return;

  const items = list.items;
  let element: LocalItem;
  let newIndex: number;
  let newListUuid: string | undefined;

  if (evt.moved) {
    element = evt.moved.element;
    newIndex = evt.moved.newIndex;
  } else if (evt.added) {
    if (!list.uuid) {
      void loadChecklists();
      return;
    }
    element = evt.added.element;
    newIndex = evt.added.newIndex;
    newListUuid = list.uuid;
  } else {
    return;
  }

  const prev = items[newIndex - 1]?.ordering;
  const next = items[newIndex + 1]?.ordering;

  let targetOrdering: number;
  if (prev === undefined && next === undefined) {
    targetOrdering = 0;
  } else if (prev === undefined) {
    targetOrdering = next! - 1;
  } else if (next === undefined) {
    targetOrdering = prev + 1;
  } else {
    targetOrdering = (prev + next) / 2;
  }

  if (items.some((i) => i !== element && i.ordering === targetOrdering)) {
    items.forEach((item, idx) => {
      item.ordering = idx * 2;
    });
    await Promise.all(
      items.map((item) =>
        Maintenance.patchTodolistItemTicket({
          path: { uuid: item.uuid },
          body: { ordering: item.ordering },
        }),
      ),
    ).catch((e: unknown) => {
      showResponseErrorToUser(e, t);
      void loadChecklists();
    });
    return;
  }

  element.ordering = targetOrdering;
  if (newListUuid) {
    element.todoListUuid = newListUuid;
    await Maintenance.patchTodolistItemTicket({
      path: { uuid: element.uuid },
      body: { ordering: targetOrdering, todoListUuid: newListUuid },
    }).catch((e: unknown) => {
      showResponseErrorToUser(e, t);
      void loadChecklists();
    });
  } else {
    await Maintenance.patchTodolistItemTicket({
      path: { uuid: element.uuid },
      body: { ordering: targetOrdering },
    }).catch((e: unknown) => {
      showResponseErrorToUser(e, t);
      void loadChecklists();
    });
  }
}

// ---- Save checklist name ----

async function saveChecklistName(list: LocalChecklist, name: string) {
  if (!list.uuid) return;
  await Maintenance.patchTasklist({
    path: { uuid: list.uuid },
    body: { name },
  }).catch((e: unknown) => {
    showResponseErrorToUser(e, t);
    throw e;
  });
  list.name = name;
}

// ---- Load ----

async function loadChecklists() {
  loadError.value = false;
  try {
    const response = await Maintenance.getTicketTasklists({
      path: { id: props.ticket.uuid },
    });
    const rawLists = response.data?.result ?? [];
    checklists.value = rawLists.map((list) => ({
      uuid: list.uuid ?? undefined,
      name: list.name ?? t("checklist"),
      ordering: list.ordering ?? 0,
      items: (list.items ?? [])
        .sort((a, b) => (a.ordering ?? 0) - (b.ordering ?? 0))
        .map((item) => ({
          uuid: item.uuid,
          todoListUuid: item.todoListUuid,
          description: item.description ?? "",
          completionTracking: item.completionTracking ?? false,
          feedback: item.feedback ?? false,
          ordering: item.ordering ?? 0,
          twin: item.twin ?? undefined,
          createdByUser: item.createdByUser,
          created: item.created,
          instance: item.instance
            ? {
                todoInstanceUuid: item.instance.todoInstanceUuid,
                completed: item.instance.completed,
                feedback: item.instance.feedback,
                status: item.instance.status,
                createdAt: item.instance.createdAt,
              }
            : null,
        })),
    }));
    await loadAssetTwins().catch((e: unknown) => {
      assetTwins.value = new Map();
      showResponseErrorToUser(e, t);
    });
  } catch {
    loadError.value = true;
  }
}

async function loadAssetTwins() {
  const assetUuids = [
    ...new Set(
      checklists.value
        .flatMap((list) => list.items)
        .filter(isLinkedAsset)
        .map((item) => item.twin?.uuid)
        .filter((uuid): uuid is string => uuid !== undefined),
    ),
  ];
  if (assetUuids.length === 0) {
    assetTwins.value = new Map();
    return;
  }
  const response = await TwinManagement.searchTwins({
    query: { page: 1, pageSize: assetUuids.length },
    body: {
      uuids: assetUuids,
      showProperties: true,
    },
  });
  assetTwins.value = new Map(
    (response.data?.result ?? []).map((asset) => [asset.uuid, asset]),
  );
}

onMounted(() => {
  void loadChecklists();
});

// ---- Add checklist ----

function openAddChecklistDialog() {
  newChecklistName.value = "";
  addChecklistDialogVisible.value = true;
}

function focusNewChecklistInput() {
  newChecklistInputRef.value?.focus();
}

async function confirmAddChecklist() {
  const name = newChecklistName.value.trim() || t("checklist");
  const lastChecklist = checklists.value[checklists.value.length - 1];
  const ordering = (lastChecklist?.ordering ?? -1) + 1;
  try {
    const response = await Maintenance.postTodolistForTicket({
      body: {
        name,
        ordering,
        ticketUuid: props.ticket.uuid,
        items: [],
      },
    });
    const created = response.data;
    checklists.value.push({
      uuid: created.uuid,
      name: created.name ?? name,
      ordering: created.ordering ?? ordering,
      items: [],
    });
    addChecklistDialogVisible.value = false;
  } catch (e) {
    showResponseErrorToUser(e, t);
  }
}

// ---- Add item ----

function openAddItemDialog(list: LocalChecklist) {
  pendingAddItemList.value = list;
  newItemDescription.value = "";
  createTicketForTask.value = false;
  resetTicketForm();
  addItemDialogVisible.value = true;
}

function focusNewItemInput() {
  newItemInputRef.value?.focus();
}

async function confirmAddItem() {
  const list = pendingAddItemList.value;
  if (!list?.uuid) return;
  let twin: TicketTwin | undefined;
  if (createTicketForTask.value) {
    const ticketTwin = getTicketTwin(
      newItemDescription.value,
      newItemDescription.value,
    );
    if (!ticketTwin) return;
    twin = ticketTwin;
  }
  try {
    const response = await Maintenance.postTodolistItemTicket({
      body: {
        todoListUuid: list.uuid,
        description: newItemDescription.value,
        completionTracking: true,
        feedback: true,
        ordering: list.items.length,
        twin,
      },
    });
    const created = response.data;
    list.items.push({
      uuid: created.uuid,
      todoListUuid: created.todoListUuid ?? list.uuid,
      description: created.description ?? newItemDescription.value,
      completionTracking: created.completionTracking ?? true,
      feedback: created.feedback ?? true,
      ordering: created.ordering ?? list.items.length,
      twin: created.twin ?? undefined,
      instance: null,
      created: created.created,
      createdByUser: created.createdByUser,
    });
    addItemDialogVisible.value = false;
  } catch (e) {
    showResponseErrorToUser(e, t);
  }
}

async function openTransferDialog(item: LocalItem) {
  transferItem.value = item;
  resetTicketForm();
  ticketDescription.value = item.description;
  try {
    await loadTicketTemplates();
    transferDialogVisible.value = true;
  } catch (e) {
    showResponseErrorToUser(e, t);
  }
}

async function confirmTransferToTicket() {
  const item = transferItem.value;
  if (!item) return;
  const twin = getTicketTwin(item.description);
  if (!twin) return;
  try {
    const response = await Maintenance.patchTodolistItemTicket({
      path: { uuid: item.uuid },
      body: { twin },
    });
    item.twin = response.data.twin ?? undefined;
    transferDialogVisible.value = false;
  } catch (e) {
    showResponseErrorToUser(e, t);
  }
}

// ---- Delete item ----

function openDeleteItemDialog(list: LocalChecklist, item: LocalItem) {
  pendingDeleteItem.value = { list, item };
  deleteItemDialogVisible.value = true;
}

async function confirmDeleteItem() {
  const pendingItem = pendingDeleteItem.value;
  if (!pendingItem) return;
  await deleteItem(pendingItem.list, pendingItem.item);
  deleteItemDialogVisible.value = false;
}

async function deleteItem(list: LocalChecklist, item: LocalItem) {
  try {
    await Maintenance.deleteTodolistItemTicket({ path: { uuid: item.uuid } });
    const idx = list.items.indexOf(item);
    if (idx >= 0) list.items.splice(idx, 1);
  } catch (e) {
    showResponseErrorToUser(e, t);
  }
}

// ---- Save description ----

async function saveItemDescription(item: LocalItem, description: string) {
  await Maintenance.patchTodolistItemTicket({
    path: { uuid: item.uuid },
    body: { description },
  }).catch((e) => {
    showResponseErrorToUser(e, t);
    throw e;
  });
  item.description = description;
}

// ---- Toggle complete ----

async function toggleItemComplete(item: LocalItem, completed: boolean) {
  if (!isTicketInProcess.value) return;
  try {
    const response = await Maintenance.patchTodolistInstanceForTicket({
      body: {
        todoListItemUuid: item.uuid,
        completed,
      },
    });
    item.instance = {
      ...item.instance,
      todoInstanceUuid: response.data.uuid,
      completed: response.data.completed,
    };
  } catch (e) {
    showResponseErrorToUser(e, t);
  }
}

// ---- Save feedback ----

async function saveItemFeedback(item: LocalItem, feedback: string) {
  if (!isTicketInProcess.value) return;
  const response = await Maintenance.patchTodolistInstanceForTicket({
    body: {
      todoListItemUuid: item.uuid,
      feedback,
    },
  }).catch((e) => {
    showResponseErrorToUser(e, t);
    throw e;
  });
  item.instance = {
    ...item.instance,
    todoInstanceUuid: response.data.uuid,
    feedback: response.data.feedback,
  };
}
</script>

<style scoped>
.checklist-content {
  background-color: white;
  border-radius: 1rem;
  padding: 1rem;
  overflow: hidden;
}

.checklist-items {
  display: flex;
  flex-direction: column;
}

.checklist-content {
  min-width: 600px;
}

@media (max-width: 1499px) {
  .checklist-view {
    min-width: 100% !important;
  }
}

.checklist-row {
  display: grid;
  grid-template-columns: 20px minmax(0, 2fr) minmax(0, 1fr) auto 20px;
  column-gap: 0.75rem;
  align-items: start;
  justify-content: start;
  overflow: hidden;
}

.checklist-row > * {
  min-width: 0;
}

.item-creator {
  grid-column: 2 / 4;
}

.checklist-row > .tasklist-checkbox {
  height: 20px;
  margin-top: 3px;
}
</style>
